// =============================================================================
// src/data/sentinel-mapping-query.ts — Queries GraphQL Sentinel (Hasura).
// =============================================================================

export const SENTINEL_DASHBOARD_QUERY = `
query SentinelDashboard($clientId: uuid!) {
  monitoring_v_sentiment_24h(where: { client_id: { _eq: $clientId } }) {
    total
    neg_pct
    neu_pct
    pos_pct
    avg_score
    avg_weighted_score
    refreshed_at
  }
  monitoring_v_feed_recent(
    where: { client_id: { _eq: $clientId } }
    order_by: { published_at: desc }
    limit: 20
  ) {
    content_id
    platform
    content_kind
    body_preview
    author_name
    likes
    published_at
    sentiment
    sent_class
    threat_level
  }
  monitoring_v_reputation_summary(where: { client_id: { _eq: $clientId } }) {
    mentions_24h
    reputation_index
    nrs
    sov
    bps
    reach_7d
  }
  monitoring_alert(
    where: { client_id: { _eq: $clientId }, acknowledged_at: { _is_null: true } }
    order_by: { created_at: desc }
    limit: 5
  ) {
    id
    severity
    title
    summary
    recommended_actions
    created_at
  }
  monitoring_v_reputation_trend(
    where: { client_id: { _eq: $clientId } }
    order_by: { ts: asc }
  ) {
    ts
    reputation_index
  }
  gold_ai_action_recommendation(
    where: { client_id: { _eq: $clientId }, status: { _eq: "suggested" } }
    order_by: { generated_at: desc }
    # 40 cabe o bridge de alertas + as ~10 táticas por seção (action_type='tactical') sem truncar.
    limit: 40
  ) {
    id
    surface_id
    action_type
    title
    summary
    recommended_actions
    evidence_ids
    confidence
    risk_level
    model
    status
    generated_at
  }
  monitoring_v_crisis_metrics_24h(where: { client_id: { _eq: $clientId } }, order_by: { gauge_0_100: desc }, limit: 1) {
    topic_id
    gauge_0_100
    mentions_24h
    mentions_delta_pct
    net_sentiment
    acceleration
    sources_unavailable
  }
  monitoring_v_content_volume_hourly(where: { client_id: { _eq: $clientId } }, order_by: { bucket_hour: asc }) {
    topic_id
    bucket_hour
    mentions
    reach_sum
    is_origin_bucket
  }
  monitoring_v_topic_amplifiers(where: { client_id: { _eq: $clientId } }, order_by: { reach: desc }, limit: 12) {
    topic_id
    actor
    kind
    mentions
    reach
    share_pct
    reach_is_portal_estimate
  }
  monitoring_v_topic_origin(where: { client_id: { _eq: $clientId } }) {
    topic_id
    outlet
    channel_label
    published_at
    published_reliable
    url
    title
    preview
  }
  monitoring_v_crisis_lifecycle_phase(where: { client_id: { _eq: $clientId } }) {
    topic_id
    detected_at
    hours_since_detection
    crisis_state
    current_phase
  }
}
`;
// (monitoring_v_reputation_delta_24h / monitoring_v_net_sentiment_delta /
// gold_daily_briefing sairam da query em 2026-07-28: alimentavam apenas o
// buildBriefingPatch, e o briefing migrou para o social_listening.)

export interface SentinelSentimentRow {
  readonly total: number;
  readonly neg_pct: number;
  readonly neu_pct: number;
  readonly pos_pct: number;
  readonly avg_score: number | null;
  readonly avg_weighted_score: number | null;
}

export interface SentinelFeedRow {
  readonly content_id: string;
  readonly platform: string | null;
  readonly content_kind: string | null;
  readonly body_preview: string | null;
  readonly author_name: string | null;
  readonly likes: number | null;
  readonly published_at: string;
  readonly sentiment: string | null;
  readonly sent_class: string;
  readonly threat_level: number;
}

export interface SentinelReputationRow {
  readonly mentions_24h: number;
  readonly reputation_index: number;
  readonly nrs: number;
  readonly sov: number;
  readonly bps: number;
  readonly reach_7d: number | null;
}

export interface SentinelAlertRow {
  readonly id: string;
  readonly severity: string;
  readonly title: string;
  readonly summary: string | null;
  readonly recommended_actions: readonly Record<string, unknown>[] | null;
}

/** Ponto da série histórica do índice (monitoring.v_reputation_trend, janela 24h). */
export interface SentinelTrendRow {
  readonly ts: string;
  readonly reputation_index: number;
}

export interface SentinelActionRecommendationRow {
  readonly id: string;
  readonly surface_id: string;
  readonly action_type: string;
  readonly title: string;
  readonly summary: string | null;
  readonly recommended_actions: readonly Record<string, unknown>[];
  readonly evidence_ids: readonly unknown[];
  readonly confidence: number;
  readonly risk_level: string;
  readonly model: string;
  readonly status: string;
  readonly generated_at: string;
}

// (SentinelReputationDeltaRow/SentinelNetSentimentDeltaRow/SentinelDailyBriefingRow
// removidos 2026-07-28 junto com os blocos da query — eram exclusivos do briefing.)

/** Métricas escalares da crise por tópico (monitoring.v_crisis_metrics_24h, migration 041). */
export interface SentinelCrisisMetricsRow {
  readonly topic_id: string;
  readonly gauge_0_100: number;
  readonly mentions_24h: number;
  readonly mentions_delta_pct: number | null;
  readonly net_sentiment: number | null;
  readonly acceleration: string;
  readonly sources_unavailable: number;
}

/** Bucket horário de volume por tópico (monitoring.v_content_volume_hourly, migration 041). */
export interface SentinelVolumeHourRow {
  readonly topic_id: string;
  readonly bucket_hour: string;
  readonly mentions: number;
  readonly reach_sum: number;
  readonly is_origin_bucket: boolean;
}

/** Amplificador de um tópico (monitoring.v_topic_amplifiers, migration 041). */
export interface SentinelAmplifierRow {
  readonly topic_id: string;
  readonly actor: string;
  readonly kind: string;
  readonly mentions: number;
  readonly reach: number;
  readonly share_pct: number | null;
  readonly reach_is_portal_estimate: boolean;
}

/** Origem do tópico — "como começou" (monitoring.v_topic_origin, migration 041). */
export interface SentinelOriginRow {
  readonly topic_id: string;
  readonly outlet: string | null;
  readonly channel_label: string | null;
  readonly published_at: string | null;
  readonly published_reliable: boolean;
  readonly url: string | null;
  readonly title: string | null;
  readonly preview: string | null;
}

/** Fase do ciclo de vida da crise (monitoring.v_crisis_lifecycle_phase, migration 041). */
export interface SentinelLifecycleRow {
  readonly topic_id: string;
  readonly detected_at: string | null;
  readonly hours_since_detection: number | null;
  readonly crisis_state: string;
  readonly current_phase: string;
}

export interface SentinelGraphqlData {
  readonly monitoring_v_sentiment_24h: readonly SentinelSentimentRow[];
  readonly monitoring_v_feed_recent: readonly SentinelFeedRow[];
  readonly monitoring_v_reputation_summary: readonly SentinelReputationRow[];
  readonly monitoring_alert: readonly SentinelAlertRow[];
  readonly monitoring_v_reputation_trend: readonly SentinelTrendRow[];
  readonly gold_ai_action_recommendation?: readonly SentinelActionRecommendationRow[];
  readonly monitoring_v_crisis_metrics_24h: readonly SentinelCrisisMetricsRow[];
  readonly monitoring_v_content_volume_hourly: readonly SentinelVolumeHourRow[];
  readonly monitoring_v_topic_amplifiers: readonly SentinelAmplifierRow[];
  readonly monitoring_v_topic_origin: readonly SentinelOriginRow[];
  readonly monitoring_v_crisis_lifecycle_phase: readonly SentinelLifecycleRow[];
}
